import { ChatActivityStatus } from '../services/chatbot-ia.service';
import { IMAGE_GENERATION_STATUS_LABEL } from './chatbot-generated-image';
import {
  formatToolActivityDetail,
  hasDistinctActivityDetail,
  processStepActionLabel,
  resolveToolTracePrimaryLabel,
} from './chatbot-tool-labels';

export type ThinkingTraceTab = 'steps' | 'reasoning' | 'search' | 'tool';

export interface ThinkingTraceRow {
  id: string;
  kind: ThinkingTraceTab;
  primary: string;
  secondary?: string;
  href?: string;
  siteHost?: string;
  isCurrent?: boolean;
}

const HIDDEN_LIVE_PHASES = new Set<ChatActivityStatus['phase']>([
  'depth',
  'writing',
]);

const SEARCH_TOOL_NAMES = new Set(['browse_web', 'search_site', 'browse_wosi_site']);

const THOUGHT_LABEL_RULES: { re: RegExp; label: string }[] = [
  {
    re: /security protocol|guardrail|travas de seguran|configura(?:c|ç)[oõ]es de seguran|admin guardrail|reviewing security|security configuration/i,
    label: 'Revisando as travas de segurança',
  },
  {
    re: /partner\s*id|identifying partner|parceiro/i,
    label: 'Identificando o parceiro',
  },
  { re: /commission|comiss[aã]o/i, label: 'Consultando as comissões' },
  { re: /voucher|coupon|cupom/i, label: 'Consultando os cupons' },
  { re: /sinistro|\bclaim\b/i, label: 'Consultando o sinistro' },
  {
    re: /ap[oó]lice|\bpolicy\b|coverage|cobertura/i,
    label: 'Consultando a apólice',
  },
  {
    re: /cliente|customer|sale\s*id|\bcpf\b|\bcnpj\b/i,
    label: 'Localizando o cliente',
  },
  { re: /\busu[aá]rio\b|\buser id\b/i, label: 'Consultando o usuário' },
  { re: /produto|\bproduct\b/i, label: 'Consultando o produto' },
  { re: /dashboard|\bkpi\b/i, label: 'Consultando o dashboard' },
  { re: /notif/i, label: 'Consultando as notificações' },
  { re: /\bblog\b|artigo/i, label: 'Preparando o conteúdo' },
  { re: /\bweb\b|browse|search|pesquis/i, label: 'Preparando a busca' },
  {
    re: /identifying|locating|looking up|finding\b/i,
    label: 'Identificando o pedido',
  },
  {
    re: /reviewing|checking|verifying|validating/i,
    label: 'Conferindo o contexto',
  },
  {
    re: /analy[sz]ing|considering|figuring|understanding|handle on the user/i,
    label: 'Analisando o pedido',
  },
  {
    re: /okay|i['’]ve got|let me|the user(?:'s)? question|i(?:['’]m| am) currently/i,
    label: 'Entendendo o pedido',
  },
];

const PT_THOUGHT_RE =
  /[áàâãéêíóôõúçÁÀÂÃÉÊÍÓÔÕÚÇ]|^(identificando|consultando|analisando|revisando|buscando|conferindo|localizando|entendendo|preparando|acionando|pensando|escolhendo|verificando|lendo|gravando|entrando|sintetizando)\b/i;

const EN_THOUGHT_RE =
  /\b(the|and|i['’]ve|i['’]m|okay|got|handle|user['’]s|question|identifying|reviewing|looking|checking|analyzing|analysing|currently|this|that|from|into|let me|i am)\b/i;

function stripThoughtMarkdown(text: string): string {
  return text
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/__([^_]+)__/g, '$1')
    .replace(/^#+\s*/gm, '')
    .replace(/`+/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function clipThoughtLine(text: string): string {
  const first = text.split(/(?<=[.!?])\s+/)[0]?.trim() || text;
  if (first.length <= 72) return first;
  return `${first.slice(0, 70).trim()}…`;
}

/** Thought do Gemini → linha curta PT-BR (sem markdown, sem ensaio em inglês). */
export function formatThoughtTracePrimary(detail?: string): string {
  const stripped = stripThoughtMarkdown(detail ?? '');
  if (!stripped) return 'Pensando…';

  for (const rule of THOUGHT_LABEL_RULES) {
    if (rule.re.test(stripped)) return rule.label;
  }

  if (!PT_THOUGHT_RE.test(stripped) && EN_THOUGHT_RE.test(stripped)) {
    return 'Pensando…';
  }

  return clipThoughtLine(stripped);
}

const IMAGE_GENERATION_TOOL = 'admin_generate_blog_image_ai';

const NON_SYSTEM_TOOL_NAMES = new Set([
  'invoke_agent',
  'agent_thought',
  'reflect_deeper',
  IMAGE_GENERATION_TOOL,
]);

function isImageGenerationStep(
  step: ChatActivityStatus | null | undefined
): boolean {
  return step?.toolName === IMAGE_GENERATION_TOOL;
}

export function isSystemToolStep(
  step: ChatActivityStatus | null | undefined
): boolean {
  if (!step?.toolName || SEARCH_TOOL_NAMES.has(step.toolName)) return false;
  if (NON_SYSTEM_TOOL_NAMES.has(step.toolName)) return false;
  if (step.phase !== 'tool' && step.phase !== 'tool_done') return false;
  return true;
}

export function isThoughtActivityStep(
  step: ChatActivityStatus | null | undefined
): boolean {
  return Boolean(
    step &&
      (step.toolName === 'agent_thought' ||
        (step.phase === 'agent' && step.label === 'Pensando…'))
  );
}

export function isHiddenThinkingStep(
  step: ChatActivityStatus | null | undefined
): boolean {
  if (!step) return false;
  if (step.phase === 'title' && step.titleIdle) return true;
  return HIDDEN_LIVE_PHASES.has(step.phase);
}

export function classifyThinkingTab(
  step: ChatActivityStatus | null | undefined
): ThinkingTraceTab {
  if (!step) return 'steps';
  if (step.phase === 'searching') return 'search';
  if (step.phase === 'reflect' || isThoughtActivityStep(step)) {
    return 'reasoning';
  }
  if (step.toolName && SEARCH_TOOL_NAMES.has(step.toolName)) return 'search';
  if (isSystemToolStep(step)) return 'tool';
  return 'steps';
}

export function inferThinkingTab(
  steps: ChatActivityStatus[],
  current: ChatActivityStatus | null | undefined
): ThinkingTraceTab {
  const probe = current && !isHiddenThinkingStep(current) ? current : steps[steps.length - 1];
  return classifyThinkingTab(probe);
}

export function visibleThinkingSteps(
  steps: ChatActivityStatus[]
): ChatActivityStatus[] {
  return steps.filter((step) => !isHiddenThinkingStep(step));
}

function stepKey(step: ChatActivityStatus, index: number): string {
  return [
    index,
    step.phase,
    step.toolName ?? '',
    step.label ?? '',
    step.detail ?? '',
    step.agentId ?? '',
  ].join(':');
}

function rowFromStep(
  step: ChatActivityStatus,
  index: number,
  isCurrent = false
): ThinkingTraceRow {
  if (step.phase === 'title') {
    const named = step.conversationTitle?.trim() || step.detail?.trim();
    return {
      id: isCurrent ? 'current' : stepKey(step, index),
      kind: 'steps',
      primary: processStepActionLabel(step, { includeDetail: false }),
      secondary: named && named !== step.label ? named : undefined,
      isCurrent,
    };
  }

  if (isImageGenerationStep(step)) {
    const primary =
      step.phase === 'tool_done'
        ? 'Imagem gerada'
        : IMAGE_GENERATION_STATUS_LABEL;
    return {
      id: isCurrent ? 'current' : stepKey(step, index),
      kind: 'steps',
      primary,
      isCurrent,
    };
  }

  const kind = classifyThinkingTab(step);
  let primary =
    kind === 'reasoning'
      ? formatThoughtTracePrimary(step.detail)
      : kind === 'tool'
        ? resolveToolTracePrimaryLabel(step)
        : processStepActionLabel(step, { includeDetail: false });

  let secondary: string | undefined;
  if (kind === 'search' && hasDistinctActivityDetail(step)) {
    const detail = step.detail?.trim();
    secondary = detail && !detail.includes('Raciocínio') ? detail : undefined;
  } else if (kind === 'tool') {
    secondary = formatToolActivityDetail(step.detail);
  } else if (kind === 'reasoning' && step.agentName?.trim()) {
    secondary = step.agentName.trim();
  } else if (kind === 'steps' && hasDistinctActivityDetail(step)) {
    secondary = step.detail?.trim();
  }

  const href = kind === 'search' ? extractBrowseUrl(step) : undefined;
  const siteHost =
    kind === 'search' ? resolveSiteHost(step.detail, href) : undefined;

  return {
    id: isCurrent ? 'current' : stepKey(step, index),
    kind,
    primary,
    secondary,
    href,
    siteHost,
    isCurrent,
  };
}

export function thinkingTraceRows(
  steps: ChatActivityStatus[],
  current: ChatActivityStatus | null | undefined,
  isActive: boolean
): ThinkingTraceRow[] {
  const visible = visibleThinkingSteps(steps);
  const rows = visible.map((step, index) => rowFromStep(step, index));

  if (isActive && current && !isHiddenThinkingStep(current)) {
    const live = rowFromStep(current, visible.length, true);
    const last = rows[rows.length - 1];
    if (
      last &&
      last.kind === live.kind &&
      (last.primary === live.primary || live.kind === 'reasoning')
    ) {
      rows[rows.length - 1] = { ...live, id: last.id, isCurrent: true };
    } else {
      rows.push(live);
    }
  }

  return rows;
}

function extractBrowseUrl(step: ChatActivityStatus): string | undefined {
  const direct = step.targetUrl?.trim();
  if (direct) {
    try {
      const normalized = /^https?:\/\//i.test(direct)
        ? direct
        : `https://${direct}`;
      const url = new URL(normalized);
      if (url.protocol === 'http:' || url.protocol === 'https:') {
        return url.href;
      }
    } catch {
      /* fall through */
    }
  }

  return extractUrlFromDetail(step.detail);
}

function extractUrlFromDetail(detail?: string): string | undefined {
  const value = detail?.trim();
  if (!value) return undefined;

  const absolute = value.match(/https?:\/\/[^\s·]+/i);
  if (absolute) {
    const url = absolute[0].replace(/[….]+$/u, '');
    return url;
  }

  // detail truncado (host/path…) não tem URL confiável — evita href quebrado
  if (value.includes('…') || /\.{3}\s*$/.test(value)) {
    return undefined;
  }

  const head = value.split(' · ')[0]?.trim() ?? '';
  if (/^[\w.-]+\.[a-z]{2,}/i.test(head)) {
    try {
      const normalized = head.includes('://') ? head : `https://${head}`;
      return new URL(normalized).href;
    } catch {
      return undefined;
    }
  }

  return undefined;
}

import {
  faviconUrlForHost,
  resolveSiteHost,
} from './chatbot-web-favicon';

export function thinkingActiveLabel(tab: ThinkingTraceTab): string {
  if (tab === 'search') return 'Buscando na web…';
  if (tab === 'tool') return 'Consultando o sistema…';
  if (tab === 'reasoning') return 'Raciocinando…';
  return 'Pensando…';
}

/** Uma linha no header colapsado — acompanha o SSE, sem dump do thought. */
export function thinkingCollapsedLiveLabel(
  steps: ChatActivityStatus[],
  current: ChatActivityStatus | null | undefined
): string {
  const visible = visibleThinkingSteps(steps);
  const probe =
    current && !isHiddenThinkingStep(current)
      ? current
      : visible[visible.length - 1];

  if (!probe) return 'Pensando…';

  if (isImageGenerationStep(probe) && probe.phase === 'tool') {
    return IMAGE_GENERATION_STATUS_LABEL;
  }

  if (isSystemToolStep(probe)) {
    return resolveToolTracePrimaryLabel(probe);
  }

  if (isThoughtActivityStep(probe)) {
    const snippet = formatThoughtTracePrimary(probe.detail);
    if (snippet && snippet !== 'Pensando…') {
      return snippet;
    }
    const name = probe.agentName?.trim();
    return name ? `Pensando · ${name}` : 'Pensando…';
  }

  const action = processStepActionLabel(probe, { includeDetail: false }).trim();
  const name = probe.agentName?.trim();
  if (name && action && !action.toLowerCase().includes(name.toLowerCase())) {
    return `${action} · ${name}`;
  }
  return action || thinkingActiveLabel(classifyThinkingTab(probe));
}

export function thinkingDoneLabel(
  _tab: ThinkingTraceTab,
  durationSeconds: number
): string {
  const duration = formatThinkingDuration(Math.max(1, durationSeconds));
  return `Pensou por ${duration}`;
}

export function formatThinkingDuration(totalSeconds: number): string {
  const seconds = Math.max(1, Math.round(totalSeconds));
  if (seconds < 60) {
    return seconds === 1 ? '1 segundo' : `${seconds} segundos`;
  }
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  if (!rest) {
    return minutes === 1 ? '1 minuto' : `${minutes} minutos`;
  }
  return `${minutes} min ${rest} s`;
}

